/**
 * Pure 5x5-subgrid footprint / occupancy contract.
 *
 * COL-004A defined the visual recommendations. COL-007 productionizes gameplay
 * occupancy via `productionFootprint`.
 */

import type { EntityKind } from '../types';
import {
  FOUNDATION_DIVISION,
  LEGACY_CELL_SIZE,
  createSubgridSpec,
  footprintCoverCells,
  type GridCoord,
  type SubgridCoord,
} from './subgrid';

export const FOOTPRINT_DIVISION = FOUNDATION_DIVISION;
export const FOOTPRINT_CELL_SIZE = LEGACY_CELL_SIZE / FOOTPRINT_DIVISION;

const FOUNDATION_SPEC = createSubgridSpec(FOUNDATION_DIVISION);

export const ROTATION_QUARTERS = [0, 90, 180, 270] as const;
export type RotationQuarter = (typeof ROTATION_QUARTERS)[number];

/** Unrotated rectangular occupancy in logical subcells. Both axes are ≥ 1. */
export interface FootprintSpec {
  cellsX: number;
  cellsZ: number;
}

/**
 * Anchor semantics: the pivot subcell the rectangle rotates around.
 *
 * Occupied cells extend `floor((n - 1) / 2)` toward −X/−Z and the remainder
 * toward +X/+Z. Odd sizes are centered on the pivot. Even sizes use the same
 * +x/+z bias as `mapLegacyCenter` so a future `slotId` bridge can keep the
 * current legacy-center cell as the pivot without a saveVersion change.
 */
export type FootprintAnchor = SubgridCoord;

export type FootprintMeasurability =
  | 'exact_static_source'
  | 'conservative_static_source'
  | 'control_point_aabb_proxy';

export interface VisualFootprintRecommendation {
  kind: EntityKind;
  spec: FootprintSpec;
  worldWidth: number;
  worldDepth: number;
  leftoverX: number;
  leftoverZ: number;
  measurability: FootprintMeasurability;
  source: string;
  /** Queue lanes, visitor paths, and motion envelopes are not this footprint. */
  excludedFromVisual: string;
  spansLegacyCell: boolean;
  subSlotGranularity: boolean;
}

function assertValidFootprintSpec(spec: FootprintSpec): void {
  if (!Number.isInteger(spec.cellsX) || spec.cellsX < 1) {
    throw new Error(`Footprint cellsX must be an integer ≥ 1, got ${spec.cellsX}`);
  }
  if (!Number.isInteger(spec.cellsZ) || spec.cellsZ < 1) {
    throw new Error(`Footprint cellsZ must be an integer ≥ 1, got ${spec.cellsZ}`);
  }
}

function assertValidRotation(rotation: RotationQuarter): void {
  if (!(ROTATION_QUARTERS as readonly number[]).includes(rotation)) {
    throw new Error(`Rotation must be 0/90/180/270, got ${rotation}`);
  }
}

export function rotatedFootprintSize(spec: FootprintSpec, rotation: RotationQuarter): FootprintSpec {
  assertValidFootprintSpec(spec);
  assertValidRotation(rotation);
  if (rotation === 90 || rotation === 270) {
    return { cellsX: spec.cellsZ, cellsZ: spec.cellsX };
  }
  return { cellsX: spec.cellsX, cellsZ: spec.cellsZ };
}

/** Min-corner of the occupied rectangle for a center-biased pivot. */
export function occupancyOriginFromAnchor(anchor: FootprintAnchor, spec: FootprintSpec): SubgridCoord {
  assertValidFootprintSpec(spec);
  return {
    sx: anchor.sx - Math.floor((spec.cellsX - 1) / 2),
    sz: anchor.sz - Math.floor((spec.cellsZ - 1) / 2),
  };
}

export function subcellKey(coord: SubgridCoord): string {
  return `${coord.sx},${coord.sz}`;
}

export function compareSubcells(a: SubgridCoord, b: SubgridCoord): number {
  return a.sx - b.sx || a.sz - b.sz;
}

export function sortSubcells(coords: readonly SubgridCoord[]): SubgridCoord[] {
  return [...coords].sort(compareSubcells);
}

function enumerateRect(origin: SubgridCoord, cellsX: number, cellsZ: number): SubgridCoord[] {
  const out: SubgridCoord[] = [];
  for (let sx = origin.sx; sx < origin.sx + cellsX; sx += 1) {
    for (let sz = origin.sz; sz < origin.sz + cellsZ; sz += 1) {
      out.push({ sx, sz });
    }
  }
  return sortSubcells(out);
}

export function occupiedSubcellsFromMinCorner(
  minCorner: SubgridCoord,
  spec: FootprintSpec,
  rotation: RotationQuarter = 0,
): SubgridCoord[] {
  const size = rotatedFootprintSize(spec, rotation);
  return enumerateRect(minCorner, size.cellsX, size.cellsZ);
}

export function occupiedSubcells(
  anchor: FootprintAnchor,
  spec: FootprintSpec,
  rotation: RotationQuarter = 0,
): SubgridCoord[] {
  const size = rotatedFootprintSize(spec, rotation);
  return enumerateRect(occupancyOriginFromAnchor(anchor, size), size.cellsX, size.cellsZ);
}

export function occupiedSetsOverlap(a: readonly SubgridCoord[], b: readonly SubgridCoord[]): boolean {
  const keys = new Set(a.map(subcellKey));
  return b.some((coord) => keys.has(subcellKey(coord)));
}

export function allowedSubcellSet(cells: readonly SubgridCoord[]): Set<string> {
  return new Set(cells.map(subcellKey));
}

export function occupiedSetContainedIn(occupied: readonly SubgridCoord[], allowed: ReadonlySet<string>): boolean {
  return occupied.every((coord) => allowed.has(subcellKey(coord)));
}

export function legacyCellOfSubcell(coord: SubgridCoord, division = FOOTPRINT_DIVISION): GridCoord {
  return {
    col: Math.floor(coord.sx / division),
    row: Math.floor(coord.sz / division),
  };
}

export function legacyCellsTouched(
  occupied: readonly SubgridCoord[],
  division = FOOTPRINT_DIVISION,
): GridCoord[] {
  const seen = new Set<string>();
  const cells: GridCoord[] = [];
  for (const coord of occupied) {
    const cell = legacyCellOfSubcell(coord, division);
    const key = `${cell.col},${cell.row}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    cells.push(cell);
  }
  return cells.sort((a, b) => a.col - b.col || a.row - b.row);
}

function recommend(
  kind: EntityKind,
  width: number,
  depth: number,
  measurability: FootprintMeasurability,
  source: string,
  excludedFromVisual: string,
): VisualFootprintRecommendation {
  const cover = footprintCoverCells(width, depth, FOUNDATION_SPEC);
  const spec: FootprintSpec = {
    cellsX: Math.max(1, cover.cellsX),
    cellsZ: Math.max(1, cover.cellsZ),
  };
  return {
    kind,
    spec,
    worldWidth: width,
    worldDepth: depth,
    leftoverX: cover.leftoverX,
    leftoverZ: cover.leftoverZ,
    measurability,
    source,
    excludedFromVisual,
    spansLegacyCell: spec.cellsX > FOOTPRINT_DIVISION || spec.cellsZ > FOOTPRINT_DIVISION,
    subSlotGranularity: spec.cellsX < FOOTPRINT_DIVISION && spec.cellsZ < FOOTPRINT_DIVISION,
  };
}

/**
 * Visual occupancy only. Queue / path exclusion is recorded but not quantized
 * into `spec`. Motion envelopes are excluded the same way.
 */
export const VISUAL_FOOTPRINT_RECOMMENDATIONS: Record<EntityKind, VisualFootprintRecommendation> = {
  starter_ride: recommend(
    'starter_ride',
    2.24 * 0.58 * 2,
    2.24 * 0.58 * 2,
    'exact_static_source',
    'carousel.ts CANOPY_RADIUS 2.24 * parkFromState CAROUSEL_SCALE 0.58 * 2',
    'queue lane / visitor path exclusion; not part of this visual spec',
  ),
  spinning_ride: recommend(
    'spinning_ride',
    1.28 * 0.95 * 2,
    1.28 * 0.95 * 2,
    'exact_static_source',
    'rides.ts base cylinder r=1.28 * parkFromState SPINNING_SCALE 0.95 * 2',
    'queue lane / visitor path exclusion; not part of this visual spec',
  ),
  pirate_ship: recommend(
    'pirate_ship',
    2 * Math.max(1.35 / 2, 0.78 + 0.17, 0.74 + 0.2, 1.14 + 0.21) * 0.96,
    2 * Math.max(1.55 / 2, 0.98 + 0.15, 2.0 / 2) * 0.96,
    'conservative_static_source',
    'rides.ts static pad 1.35x1.55, A-frame feet ±0.78+0.17, axle length 2.0, boat at rest; * PIRATE_SCALE 0.96',
    'PIRATE_SWING_AMPLITUDE motion envelope; 30° production yaw; queue / path exclusion',
  ),
  ferris_wheel: recommend(
    'ferris_wheel',
    2 * (1.42 + 0.23) * 1.08,
    0.7 * 1.08,
    'exact_static_source',
    'rides.ts vertical wheel diameter 2*(1.42+gondola half 0.23) * FERRIS_SCALE 1.08; Z pad 0.7*1.08. COL-003A listed the half-span as width; occupancy uses the full X diameter.',
    'queue lane / visitor path exclusion; gondola swing is in the wheel plane, not extra XZ',
  ),
  roller_coaster: recommend(
    'roller_coaster',
    (4.4 - -3.7 + 2 * 0.15) * 0.5,
    (0 - -4.45 + 2 * 0.15) * 0.5,
    'control_point_aabb_proxy',
    'coaster.ts track control-point AABB x[-3.7,4.4] z[-4.45,0] + RAIL_OFFSET 0.15 * COASTER_SCALE 0.5. Not a baked-mesh Box3. COL-002 0.68*2 core keepout is queue exclusion, not this visual spec.',
    'COL-002 core keepout 0.68*2; queue / path exclusion; cars beyond the rail offset',
  ),
  snack_kiosk: recommend(
    'snack_kiosk',
    1.55 * 0.9,
    1.25 * 0.9,
    'exact_static_source',
    'structures.ts pad 1.55x1.25 * parkFromState KIOSK_SCALE 0.9',
    'queue lane / visitor path exclusion; not part of this visual spec',
  ),
  drink_kiosk: recommend(
    'drink_kiosk',
    1.45 * 0.9,
    1.2 * 0.9,
    'exact_static_source',
    'structures.ts pad 1.45x1.2 * parkFromState KIOSK_SCALE 0.9',
    'queue lane / visitor path exclusion; not part of this visual spec',
  ),
  bench: recommend(
    'bench',
    0.86,
    0.3,
    'exact_static_source',
    'props.ts seat 0.86x0.30 (unscaled in parkFromState; rotY 0.35 is presentation, not a contract quarter-turn)',
    'queue / path exclusion; production 0.35 rad yaw is not a 0/90/180/270 contract rotation',
  ),
  tree: recommend(
    'tree',
    (0.52 + 0.26) * 1.08 * 2,
    (0.52 + 0.16) * 1.08 * 2,
    'conservative_static_source',
    'props.ts round-tree foliage r=0.52 plus offsets 0.26/0.16 * parkFromState scale 1.08 * 2 (COL-003A conservative extents)',
    'queue / path exclusion; pine proto is not the catalog placement',
  ),
  lamp: recommend(
    'lamp',
    0.16 * 2,
    0.16 * 2,
    'exact_static_source',
    'props.ts canopy cone r=0.16 (unscaled in parkFromState)',
    'queue / path exclusion; not part of this visual spec',
  ),
};

export function visualFootprintRecommendation(kind: EntityKind): VisualFootprintRecommendation {
  const recommendation = VISUAL_FOOTPRINT_RECOMMENDATIONS[kind];
  if (!recommendation) {
    throw new Error(`No visual footprint recommendation for ${kind}`);
  }
  return recommendation;
}

/**
 * Gameplay occupancy. Small props follow the COL-007 user cap (bench/tree ≤ 2,
 * lamp 1x1). Rides and kiosks keep the accepted visual-recommendation specs.
 */
export const PRODUCTION_FOOTPRINTS: Record<EntityKind, FootprintSpec> = {
  starter_ride: { cellsX: 5, cellsZ: 5 },
  spinning_ride: { cellsX: 5, cellsZ: 5 },
  pirate_ship: { cellsX: 5, cellsZ: 5 },
  ferris_wheel: { cellsX: 5, cellsZ: 3 },
  roller_coaster: VISUAL_FOOTPRINT_RECOMMENDATIONS.roller_coaster.spec,
  snack_kiosk: VISUAL_FOOTPRINT_RECOMMENDATIONS.snack_kiosk.spec,
  drink_kiosk: VISUAL_FOOTPRINT_RECOMMENDATIONS.drink_kiosk.spec,
  bench: { cellsX: 2, cellsZ: 1 },
  tree: { cellsX: 2, cellsZ: 1 },
  lamp: { cellsX: 1, cellsZ: 1 },
};

export function productionFootprint(kind: EntityKind): FootprintSpec {
  const spec = PRODUCTION_FOOTPRINTS[kind];
  if (!spec) {
    throw new Error(`No production footprint for ${kind}`);
  }
  return spec;
}
